import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Box, CreditCard, Loader2, MoreHorizontal, Plus, RefreshCw, Users, X } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

import AdminShell from '../components/AdminShell';
import PaginationControls from '../components/PaginationControls';
import FeatureSchemaBuilder from '../components/FeatureSchemaBuilder';
import { useAdminCatalog } from '../hooks/useAdminCatalog';
import { useAdminProducts } from '../hooks/useAdminProducts';
import { useLockBodyScroll } from '../hooks/useLockBodyScroll';
import { ROUTES } from '../constants/routes';
import type { ProductFeatureDefinition } from '../api/admin';

// ─── Types ──────────────────────────────────────────────────────────────────

type ModalMode = 'create' | 'edit';

interface ProductDraft {
  code: string;
  name: string;
  description: string;
  featureSchema: ProductFeatureDefinition[];
  status: string;
}

const emptyDraft = (): ProductDraft => ({
  code: '',
  name: '',
  description: '',
  featureSchema: [],
  status: 'active',
});

// ─── Status badge ────────────────────────────────────────────────────────────

const StatusBadge = ({ status }: { status: string }) => {
  const colors: Record<string, string> = {
    active:   'border-emerald-500/30 bg-emerald-500/10 text-emerald-400',
    draft:    'border-amber-500/30 bg-amber-500/10 text-amber-400',
    inactive: 'border-zinc-600 bg-zinc-800 text-zinc-400',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full border text-[11px] font-medium ${colors[status] ?? colors.inactive}`}>
      {status}
    </span>
  );
};

// ─── Page ────────────────────────────────────────────────────────────────────

export default function ProductsPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState('');
  const {
    products, pagination, loading, creating, updating,
    refresh, createProduct, saveProduct,
  } = useAdminProducts(query, page, 10);
  const { removeProduct, mutating: deletingProduct } = useAdminCatalog('viewer-lens', undefined, 1, 10, {
    loadPlans: false,
    loadSubscriptions: false,
    enableAutoRefresh: false,
  });

  // ── Modal state ──
  const [modalMode, setModalMode] = useState<ModalMode>('create');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [draft, setDraft] = useState<ProductDraft>(emptyDraft());

  // ── Confirm delete ──
  const [confirmDelete, setConfirmDelete] = useState<{ id: number; name: string } | null>(null);

  // ── Context menu ──
  const [openMenu, setOpenMenu] = useState<null | {
    top: number; right: number;
    items: Array<{ label: string; tone?: 'danger'; disabled?: boolean; onClick: () => void }>;
  }>(null);

  // ── Scroll Lock ── 
  useLockBodyScroll(modalOpen || Boolean(confirmDelete) || Boolean(openMenu));

  // Close context menu on any outside interaction
  useEffect(() => {
    if (!openMenu) return;
    const close = () => setOpenMenu(null);
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    window.addEventListener('resize', close);
    window.addEventListener('scroll', close, true);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', esc);
      window.removeEventListener('resize', close);
      window.removeEventListener('scroll', close, true);
    };
  }, [openMenu]);

  // Close modal on Escape
  useEffect(() => {
    if (!modalOpen) return;
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') closeModal(); };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [modalOpen]);

  // ── Helpers ──
  const openCreateModal = () => {
    setModalMode('create');
    setEditingId(null);
    setDraft(emptyDraft());
    setModalOpen(true);
  };

  const openEditModal = (product: typeof products[number]) => {
    setModalMode('edit');
    setEditingId(product.id);
    setDraft({
      code: product.code,
      name: product.name,
      description: product.description || '',
      featureSchema: product.featureSchema || [],
      status: product.status,
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingId(null);
  };

  const openActionMenu = (
    e: React.MouseEvent<HTMLButtonElement>,
    items: typeof openMenu extends null ? never : NonNullable<typeof openMenu>['items'],
  ) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setOpenMenu({ top: rect.bottom + 6, right: Math.max(window.innerWidth - rect.right, 16), items });
  };

  // ── Handlers ──
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (modalMode === 'create') {
        await createProduct({
          code:          draft.code.trim(),
          name:          draft.name.trim(),
          description:   draft.description.trim(),
          featureSchema: draft.featureSchema,
          status:        draft.status,
        });
        toast.success('Product created.');
      } else if (editingId !== null) {
        await saveProduct(editingId, {
          name:          draft.name.trim(),
          description:   draft.description.trim(),
          featureSchema: draft.featureSchema,
          status:        draft.status,
        });
        toast.success('Product updated.');
      }
      closeModal();
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message || err?.message || 'Something went wrong.');
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await removeProduct(confirmDelete.id);
      toast.success('Product removed.');
      setConfirmDelete(null);
      await refresh();
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message || err?.message || 'Failed to remove product.');
    }
  };

  const goToBilling = (code: string) =>
    navigate(`${ROUTES.SUBSCRIPTIONS}?product=${encodeURIComponent(code)}`);

  const isBusy = creating || updating;

  // ── Render ──
  return (
    <AdminShell
      title="Products"
      description="Manage product definitions. Each product has its own feature schema and subscription plans."
      actions={(
        <>
          <button
            onClick={() => refresh().catch(() => undefined)}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-zinc-700 text-zinc-300 hover:border-zinc-600 hover:text-zinc-100 transition-colors text-xs"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
          <input
            value={query}
            onChange={(e) => { setQuery(e.target.value); setPage(1); }}
            placeholder="Search products…"
            className="h-9 px-3 rounded-lg border border-zinc-700 bg-zinc-900 text-zinc-100 text-xs w-44"
          />
          <button
            onClick={openCreateModal}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-amber-500 hover:bg-amber-600 text-black font-semibold transition-colors text-xs"
          >
            <Plus className="w-3.5 h-3.5" /> Product
          </button>
        </>
      )}
    >
      {loading && !products.length ? (
        <div className="flex items-center gap-2 text-zinc-500 py-6">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading products…
        </div>
      ) : null}

      {!loading && products.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-zinc-800 p-12 text-center">
          <Box className="w-8 h-8 text-zinc-700 mx-auto mb-3" />
          <p className="text-sm text-zinc-400">No products found.</p>
          <button
            onClick={openCreateModal}
            className="mt-4 inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-amber-500 hover:bg-amber-600 text-black text-xs font-semibold"
          >
            <Plus className="w-3.5 h-3.5" /> Create first product
          </button>
        </div>
      ) : null}

      {products.length > 0 ? (
        <section className="rounded-2xl border border-zinc-800 bg-zinc-900 overflow-hidden">
          <div className="divide-y divide-zinc-800">
            {products.map((product) => (
              <div key={product.id} className="flex items-start gap-4 p-5 hover:bg-zinc-800/30 transition-colors">
                {/* Icon */}
                <div className="w-10 h-10 rounded-xl bg-zinc-800 border border-zinc-700 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Box className="w-4 h-4 text-zinc-400" />
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-zinc-100">{product.name}</p>
                    <StatusBadge status={product.status} />
                    <span className="text-xs text-zinc-600 font-mono">{product.code}</span>
                  </div>
                  {product.description ? (
                    <p className="text-xs text-zinc-400 mt-1 line-clamp-2">{product.description}</p>
                  ) : null}

                  {/* Feature schema pills */}
                  {(product.featureSchema || []).length > 0 ? (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {(product.featureSchema || []).slice(0, 6).map((f) => (
                        <span key={f.key} className="px-2 py-0.5 rounded-full border border-zinc-700 bg-zinc-950 text-[10px] text-zinc-400 font-mono">
                          {f.key}
                          <span className={`ml-1 ${
                            f.type === 'boolean' ? 'text-emerald-500' :
                            f.type === 'number'  ? 'text-sky-500' :
                            f.type === 'select'  ? 'text-purple-500' : 'text-zinc-500'
                          }`}>{f.type}</span>
                        </span>
                      ))}
                      {(product.featureSchema || []).length > 6 ? (
                        <span className="px-2 py-0.5 text-[10px] text-zinc-600">
                          +{(product.featureSchema || []).length - 6} more
                        </span>
                      ) : null}
                    </div>
                  ) : (
                    <p className="mt-1.5 text-[11px] text-zinc-600">No feature schema defined.</p>
                  )}

                  {/* Stats */}
                  <div className="mt-3 flex items-center gap-4 text-xs text-zinc-500">
                    <span className="flex items-center gap-1">
                      <CreditCard className="w-3 h-3" /> {product.planCount} plan{product.planCount !== 1 ? 's' : ''}
                    </span>
                    <span className="flex items-center gap-1">
                      <Users className="w-3 h-3" /> {product.subscriptionCount} subscription{product.subscriptionCount !== 1 ? 's' : ''}
                    </span>
                    {product.activeSubscriptionCount > 0 ? (
                      <span className="text-emerald-500">{product.activeSubscriptionCount} active</span>
                    ) : null}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => goToBilling(product.code)}
                    className="h-8 px-3 rounded-lg border border-amber-500/30 bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 text-xs transition-colors"
                  >
                    Billing
                  </button>
                  <button
                    type="button"
                    onClick={(e) => openActionMenu(e, [
                      { label: 'Edit product', onClick: () => { openEditModal(product); setOpenMenu(null); } },
                      {
                        label: 'Delete product',
                        tone: 'danger',
                        disabled: deletingProduct,
                        onClick: () => { setConfirmDelete({ id: product.id, name: product.name }); setOpenMenu(null); },
                      },
                    ])}
                    className="h-8 w-8 inline-flex items-center justify-center rounded-lg border border-zinc-700 text-zinc-400 hover:text-zinc-100 transition-colors"
                  >
                    <MoreHorizontal className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div className="px-5 py-3 border-t border-zinc-800">
            <PaginationControls pagination={pagination} onPageChange={setPage} />
          </div>
        </section>
      ) : null}

      {/* ── Create / Edit modal ─────────────────────────────────────────── */}
      {modalOpen ? createPortal(
        <div
          className="fixed inset-0 z-[55] flex items-start justify-center bg-black/70 backdrop-blur-sm px-4 py-10 overflow-y-auto"
          onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}
        >
          <div className="w-full max-w-2xl rounded-2xl border border-zinc-800 bg-zinc-900 shadow-2xl">
            {/* Modal header */}
            <div className="flex items-start justify-between gap-4 px-6 pt-6 pb-5 border-b border-zinc-800">
              <div>
                <h2 className="text-base font-semibold text-zinc-100">
                  {modalMode === 'create' ? 'New Product' : `Edit — ${draft.name || 'product'}`}
                </h2>
                <p className="text-xs text-zinc-500 mt-1">
                  {modalMode === 'create'
                    ? 'Define the product code, metadata and the feature schema that all its plans will use.'
                    : 'Update product metadata and the feature schema. Existing plan features remain until you save a plan.'}
                </p>
              </div>
              <button
                type="button"
                onClick={closeModal}
                className="h-8 w-8 flex items-center justify-center rounded-lg border border-zinc-700 text-zinc-400 hover:text-zinc-100 transition-colors flex-shrink-0"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Modal body */}
            <form onSubmit={handleSubmit} className="px-6 py-5 space-y-5">
              {/* Basic info */}
              <div className="space-y-3">
                <p className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Basic info</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {modalMode === 'create' ? (
                    <div>
                      <label className="block text-[11px] text-zinc-500 mb-1">Product code <span className="text-red-400">*</span></label>
                      <input
                        value={draft.code}
                        onChange={(e) => setDraft((p) => ({ ...p, code: e.target.value }))}
                        placeholder="e.g. my-product"
                        required
                        className="w-full h-10 px-3 bg-zinc-950 border border-zinc-700 rounded-xl text-sm text-zinc-100 font-mono focus:border-amber-500 focus:outline-none"
                      />
                      <p className="mt-1 text-[10px] text-zinc-600">Lowercase slug. Cannot be changed later.</p>
                    </div>
                  ) : (
                    <div>
                      <label className="block text-[11px] text-zinc-500 mb-1">Product code</label>
                      <div className="h-10 px-3 bg-zinc-950/50 border border-zinc-800 rounded-xl text-sm text-zinc-500 font-mono flex items-center">
                        {draft.code}
                      </div>
                    </div>
                  )}
                  <div>
                    <label className="block text-[11px] text-zinc-500 mb-1">Name <span className="text-red-400">*</span></label>
                    <input
                      value={draft.name}
                      onChange={(e) => setDraft((p) => ({ ...p, name: e.target.value }))}
                      placeholder="e.g. Comment Goldmine"
                      required
                      className="w-full h-10 px-3 bg-zinc-950 border border-zinc-700 rounded-xl text-sm text-zinc-100 focus:border-amber-500 focus:outline-none"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] text-zinc-500 mb-1">Description</label>
                  <textarea
                    value={draft.description}
                    onChange={(e) => setDraft((p) => ({ ...p, description: e.target.value }))}
                    placeholder="Short description visible in admin only"
                    rows={2}
                    className="w-full px-3 py-2 bg-zinc-950 border border-zinc-700 rounded-xl text-sm text-zinc-100 focus:border-amber-500 focus:outline-none resize-none"
                  />
                </div>
                <div>
                  <label className="block text-[11px] text-zinc-500 mb-1">Status</label>
                  <select
                    value={draft.status}
                    onChange={(e) => setDraft((p) => ({ ...p, status: e.target.value }))}
                    className="h-10 px-3 bg-zinc-950 border border-zinc-700 rounded-xl text-sm text-zinc-100 focus:border-amber-500 focus:outline-none"
                  >
                    <option value="active">active</option>
                    <option value="draft">draft</option>
                    {modalMode === 'edit' && <option value="inactive">inactive</option>}
                  </select>
                </div>
              </div>

              {/* Divider */}
              <div className="border-t border-zinc-800" />

              {/* Feature schema */}
              <div className="space-y-3">
                <p className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Feature schema</p>
                <FeatureSchemaBuilder
                  value={draft.featureSchema}
                  onChange={(schema) => setDraft((p) => ({ ...p, featureSchema: schema }))}
                />
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end gap-2 pt-2 border-t border-zinc-800">
                <button
                  type="button"
                  onClick={closeModal}
                  className="h-10 px-4 rounded-xl border border-zinc-700 text-zinc-300 hover:text-zinc-100 text-sm transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isBusy}
                  className="h-10 px-5 rounded-xl bg-amber-500 hover:bg-amber-600 text-black font-semibold text-sm disabled:opacity-50 transition-colors"
                >
                  {isBusy
                    ? (modalMode === 'create' ? 'Creating…' : 'Saving…')
                    : (modalMode === 'create' ? 'Create Product' : 'Save Changes')}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body,
      ) : null}

      {/* ── Delete confirm modal ────────────────────────────────────────── */}
      {confirmDelete ? createPortal(
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
          onClick={(e) => { if (e.target === e.currentTarget) setConfirmDelete(null); }}
        >
          <div className="w-full max-w-sm rounded-2xl border border-zinc-800 bg-zinc-900 p-6 shadow-2xl">
            <h3 className="text-base font-semibold text-zinc-100">Delete product?</h3>
            <p className="mt-2 text-sm text-zinc-400">
              <span className="font-medium text-zinc-200">{confirmDelete.name}</span> will be deactivated if it has active subscriptions, or permanently deleted otherwise.
            </p>
            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmDelete(null)}
                className="h-9 px-4 rounded-xl border border-zinc-700 text-zinc-300 text-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => { handleDelete().catch(() => undefined); }}
                disabled={deletingProduct}
                className="h-9 px-4 rounded-xl bg-red-500/90 hover:bg-red-500 text-white text-sm font-semibold disabled:opacity-50"
              >
                {deletingProduct ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      ) : null}

      {/* ── Context menu ────────────────────────────────────────────────── */}
      {openMenu ? createPortal(
        <div
          className="fixed z-[65] w-44 rounded-xl border border-zinc-700 bg-zinc-950 shadow-2xl overflow-hidden"
          style={{ top: openMenu.top, right: openMenu.right }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {openMenu.items.map((item) => (
            <button
              key={item.label}
              type="button"
              disabled={item.disabled}
              onClick={item.onClick}
              className={`w-full px-3 py-2.5 text-left text-xs transition-colors disabled:opacity-50 ${
                item.tone === 'danger'
                  ? 'text-red-300 hover:bg-red-500/10'
                  : 'text-zinc-200 hover:bg-zinc-900'
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>,
        document.body,
      ) : null}
    </AdminShell>
  );
}
